import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useStatuts } from "@/context/StatutsContext";
import { X, Loader2, Coins, AlertTriangle, Save, MessageSquare, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { TimeInput } from "@/components/TimeInput";
import { formatDateTime } from "@/lib/statuts";

const DOCS = ["CNI — Carte d'identité", "Avis d'imposition", "Taxe foncière", "Acte notarié", "Kbis SCI"];
const DOCS_LOCATAIRE = ["Avis d'imposition locataire", "CNI locataire"];

export default function VisiteFormModal({ mode = "create", visite = null, onClose, onSaved }) {
  const { user } = useAuth();
  const { statuts } = useStatuts();
  const isEdit = mode === "edit";
  const isAdmin = user.role === "admin";
  const isPartner = user.role === "installateur";
  const canTech = ["admin", "planification"].includes(user.role);

  const [ents, setEnts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [solde, setSolde] = useState(null);

  const [clientMode, setClientMode] = useState("existing"); // create only: existing | new
  const [selectedClientId, setSelectedClientId] = useState(isEdit ? (visite?.client_id || "") : "");

  const cl = (isEdit && visite?.client) || {};
  const [c, setC] = useState({
    prenom: cl.prenom || "", nom: cl.nom || "", telephone: cl.telephone || "", email: cl.email || "",
    adresse: (isEdit ? (visite?.adresse || cl.adresse) : "") || "",
    complement_adresse: (isEdit ? (visite?.complement_adresse || cl.complement_adresse) : "") || "",
    code_postal: (isEdit ? (visite?.code_postal || cl.code_postal) : "") || "",
    ville: (isEdit ? (visite?.ville || cl.ville) : "") || "",
  });

  const [f, setF] = useState({
    installateur_id: isEdit ? (visite?.installateur_id || "") : (isPartner ? "" : ""),
    technicien_id: isEdit ? (visite?.technicien_id || "") : "",
    type_visite: isEdit ? (visite?.type_visite || "VT 174 Smart") : "VT 174 Smart",
    date_visite: isEdit ? (visite?.date_visite || "") : "",
    heure_debut: isEdit ? (visite?.heure_debut || "") : "",
    heure_fin: isEdit ? (visite?.heure_fin || "") : "",
    prix: isEdit && visite?.prix != null ? String(visite.prix) : "",
    statut: isEdit ? (visite?.statut || "") : "",
  });

  const [docsDemandes, setDocsDemandes] = useState(isEdit ? (visite?.documents_demandes || []) : []);
  const toggleDoc = (d) => setDocsDemandes((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);

  const [comments, setComments] = useState(isEdit ? (visite?.commentaires_list || []) : []);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    api.get("/entreprises").then((r) => {
      setEnts(r.data);
      if (!isEdit && isPartner && r.data[0]) setF((x) => ({ ...x, installateur_id: r.data[0].id }));
    }).catch(() => {});
    if (!isPartner) api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {});
    api.get("/meta").then((r) => setTypes(r.data.types_visite)).catch(() => {});
    if (!isEdit) api.get("/clients?limit=200").then((r) => setClients(r.data.items)).catch(() => {});
    if (isPartner && user.entreprise_id) api.get(`/partenaires/${user.entreprise_id}/solde`).then((r) => setSolde(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prepaiement = solde?.prepayment_mode === "prepaiement";
  const soldeType = prepaiement ? (solde.soldes || []).find((b) => b.vt_type === f.type_visite) : null;
  const restant = prepaiement ? (soldeType ? soldeType.restant : 0) : null;

  // Statut par défaut à la création : premier statut actif
  useEffect(() => {
    if (!isEdit && !f.statut && statuts.length) setF((x) => ({ ...x, statut: statuts[0].code }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuts]);

  // Préremplir les coordonnées depuis le client existant sélectionné (mode création)
  useEffect(() => {
    if (isEdit || clientMode !== "existing" || !selectedClientId) return;
    const cc = clients.find((x) => x.id === selectedClientId);
    if (cc) setC({
      prenom: cc.prenom || "", nom: cc.nom || "", telephone: cc.telephone || "", email: cc.email || "",
      adresse: cc.adresse || "", complement_adresse: cc.complement_adresse || "", code_postal: cc.code_postal || "", ville: cc.ville || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  const addComment = async () => {
    const txt = newComment.trim();
    if (!txt) return;
    if (isEdit) {
      setAddingComment(true);
      try {
        const { data } = await api.post(`/visites/${visite.id}/commentaires`, { texte: txt });
        setComments((s) => [...s, data]);
        setNewComment("");
      } catch (err) { toast.error(apiError(err)); } finally { setAddingComment(false); }
    } else {
      setComments((s) => [...s, { id: `local-${Date.now()}`, texte: txt, auteur_nom: `${user.prenom} ${user.nom}`, date: new Date().toISOString(), local: true }]);
      setNewComment("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        if (visite.client_id) {
          await api.put(`/clients/${visite.client_id}`, {
            prenom: c.prenom, nom: c.nom, telephone: c.telephone, email: c.email,
            adresse: c.adresse, complement_adresse: c.complement_adresse, code_postal: c.code_postal, ville: c.ville,
          });
        }
        const payload = {
          type_visite: f.type_visite, installateur_id: f.installateur_id || undefined,
          adresse: c.adresse, complement_adresse: c.complement_adresse, code_postal: c.code_postal, ville: c.ville,
          documents_demandes: docsDemandes,
        };
        if (isAdmin && f.prix !== "") payload.prix = Number(f.prix);
        await api.put(`/visites/${visite.id}`, payload);
        // Planning (date / heure / technicien) via endpoint dédié pour conflits + historique
        const planPatch = {};
        if ((f.date_visite || "") !== (visite.date_visite || "")) planPatch.date_visite = f.date_visite || null;
        if ((f.heure_debut || "") !== (visite.heure_debut || "")) planPatch.heure_debut = f.heure_debut;
        if ((f.heure_fin || "") !== (visite.heure_fin || "")) planPatch.heure_fin = f.heure_fin;
        if (canTech && (f.technicien_id || "") !== (visite.technicien_id || "")) planPatch.technicien_id = f.technicien_id;
        if (Object.keys(planPatch).length) {
          try {
            await api.patch(`/visites/${visite.id}/planning`, planPatch);
          } catch (err2) {
            if (err2?.response?.status === 409) {
              if (window.confirm(apiError(err2) + "\n\nForcer quand même ?")) {
                await api.patch(`/visites/${visite.id}/planning`, { ...planPatch, force: true });
              } else { setLoading(false); return; }
            } else { throw err2; }
          }
        }
        if ((f.statut || "") && f.statut !== visite.statut) {
          await api.patch(`/visites/${visite.id}/statut`, { statut: f.statut });
        }
        toast.success("Rendez-vous mis à jour");
        onSaved();
      } else {
        let clientId = selectedClientId;
        if (clientMode === "new") {
          if (!c.prenom || !c.nom) { toast.error("Prénom et nom du client requis"); setLoading(false); return; }
          const { data: nc } = await api.post("/clients", {
            prenom: c.prenom, nom: c.nom, telephone: c.telephone, email: c.email,
            adresse: c.adresse, complement_adresse: c.complement_adresse, code_postal: c.code_postal, ville: c.ville,
          });
          clientId = nc.id;
        }
        if (!clientId) { toast.error("Sélectionnez un client"); setLoading(false); return; }
        const installId = isPartner ? undefined : f.installateur_id;
        if (!isPartner && !installId) { toast.error("Sélectionnez un partenaire"); setLoading(false); return; }
        const payload = {
          client_id: clientId, installateur_id: installId, technicien_id: canTech ? (f.technicien_id || null) : null,
          type_visite: f.type_visite, statut: f.statut || "NOUVEAU",
          date_visite: f.date_visite || null, heure_debut: f.date_visite ? f.heure_debut : null, heure_fin: f.date_visite ? f.heure_fin : null,
          adresse: c.adresse, complement_adresse: c.complement_adresse,
          code_postal: c.code_postal, ville: c.ville, documents_demandes: docsDemandes,
        };
        if (isAdmin && f.prix !== "") payload.prix = Number(f.prix);
        const { data: nv } = await api.post("/visites", payload);
        let commentFailed = false;
        for (const cm of comments) {
          try { await api.post(`/visites/${nv.id}/commentaires`, { texte: cm.texte }); } catch (e) { commentFailed = true; }
        }
        if (commentFailed) toast.warning("Rendez-vous créé, mais un commentaire n'a pas pu être enregistré.");
        onSaved();
      }
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const partenaireLabel = ents.find((e) => e.id === f.installateur_id)?.raison_sociale;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_20px_50px_rgb(0,0,0,0.15)]" data-testid={isEdit ? "edit-visite-modal" : "visite-form"}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">{isEdit ? `Modifier le rendez-vous ${visite.numero_dossier || ""}` : "Nouveau rendez-vous"}</h2>
          <button type="button" onClick={onClose}><X className="h-5 w-5 text-slate-400 hover:text-slate-700" /></button>
        </div>

        {!isEdit && isPartner && prepaiement && (
          <div data-testid="vf-solde-banner" className={`mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 ${restant === 0 ? "border-rose-300 bg-rose-50" : "border-violet-200 bg-violet-50"}`}>
            <div className="flex items-center gap-2 text-sm">
              {restant === 0 ? <AlertTriangle className="h-4 w-4 text-rose-500" /> : <Coins className="h-4 w-4 text-violet-600" />}
              <span className={restant === 0 ? "text-rose-700" : "text-violet-700"}>
                {restant === 0 ? <>Vous n'avez plus de jetons pour <b>{f.type_visite}</b>.</> : <>Jetons disponibles pour <b>{f.type_visite}</b> : <b>{restant ?? "—"}</b></>}
              </span>
            </div>
            <Link to="/mes-jetons" className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">Acheter des jetons</Link>
          </div>
        )}

        {/* PARTIE 1 — CLIENT */}
        <SectionTitle>Client</SectionTitle>
        {!isEdit && (
          <div className="mb-3 flex gap-2">
            <button type="button" onClick={() => setClientMode("existing")} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${clientMode === "existing" ? "bg-[#0A0A0A] text-white" : "border border-slate-200 text-slate-600"}`}>Client existant</button>
            <button type="button" onClick={() => setClientMode("new")} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${clientMode === "new" ? "bg-[#0A0A0A] text-white" : "border border-slate-200 text-slate-600"}`}>Nouveau client</button>
          </div>
        )}
        {!isEdit && clientMode === "existing" ? (
          <Field label="Client">
            <select data-testid="vf-client" required value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)} className="ef">
              <option value="">Sélectionner…</option>
              {clients.map((cc) => <option key={cc.id} value={cc.id}>{cc.prenom} {cc.nom} — {cc.code_postal} {cc.ville}</option>)}
            </select>
          </Field>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prénom"><input required data-testid="vf-prenom" className="ef" value={c.prenom} onChange={(e) => setC({ ...c, prenom: e.target.value })} /></Field>
            <Field label="Nom"><input required data-testid="vf-nom" className="ef" value={c.nom} onChange={(e) => setC({ ...c, nom: e.target.value })} /></Field>
            <Field label="Téléphone"><input data-testid="vf-tel" className="ef" value={c.telephone} onChange={(e) => setC({ ...c, telephone: e.target.value })} /></Field>
            <Field label="Email"><input type="email" className="ef" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></Field>
            <Field label="Adresse"><input className="ef" value={c.adresse} onChange={(e) => setC({ ...c, adresse: e.target.value })} /></Field>
            <Field label="Complément d'adresse"><input className="ef" value={c.complement_adresse} onChange={(e) => setC({ ...c, complement_adresse: e.target.value })} /></Field>
            <CpVille cp={c.code_postal} ville={c.ville} onCp={(v) => setC((x) => ({ ...x, code_postal: v }))} onVille={(v) => setC((x) => ({ ...x, ville: v }))} />
          </div>
        )}

        {/* PARTIE 2 — RENDEZ-VOUS */}
        <SectionTitle className="mt-5">Rendez-vous</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          {canTech && (
            <Field label="Technicien affecté">
              <select data-testid="vf-technicien" className="ef" value={f.technicien_id} onChange={(e) => setF({ ...f, technicien_id: e.target.value })}>
                <option value="">Non affecté</option>
                {techs.map((t) => <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
              </select>
            </Field>
          )}
          <Field label="Type de VT">
            <select data-testid="vf-type" className="ef" value={f.type_visite} onChange={(e) => setF({ ...f, type_visite: e.target.value })}>
              {[f.type_visite, ...types.filter((t) => t !== f.type_visite)].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Partenaire">
            {isPartner ? (
              <input disabled className="ef bg-slate-100" value={user.entreprise?.raison_sociale || ents[0]?.raison_sociale || partenaireLabel || "Votre entreprise"} />
            ) : (
              <select data-testid="vf-installateur" required={!isEdit} className="ef" value={f.installateur_id} onChange={(e) => setF({ ...f, installateur_id: e.target.value })}>
                <option value="">Sélectionner…</option>
                {ents.map((e) => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}
              </select>
            )}
          </Field>
          <Field label="Date de visite"><DatePicker value={f.date_visite} onChange={(v) => setF({ ...f, date_visite: v })} testid="vf-date" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Heure début"><TimeInput testid="vf-heure-debut" className="ef" value={f.heure_debut} onChange={(v) => setF({ ...f, heure_debut: v, heure_fin: f.heure_fin || addHour(v) })} /></Field>
            <Field label="Heure fin"><TimeInput testid="vf-heure-fin" className="ef" value={f.heure_fin} onChange={(v) => setF({ ...f, heure_fin: v })} /></Field>
          </div>
          {isAdmin && <Field label="Prix (€)"><input type="number" step="0.01" data-testid="vf-prix" className="ef" value={f.prix} onChange={(e) => setF({ ...f, prix: e.target.value })} /></Field>}
          <Field label="Statut">
            <select data-testid="vf-statut" className="ef" value={f.statut} onChange={(e) => setF({ ...f, statut: e.target.value })}>
              {(statuts.some((s) => s.code === f.statut) ? statuts : [{ code: f.statut, label: visite?.statut_label || f.statut }, ...statuts]).map((s) => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* PARTIE 3 — DOCUMENTS DEMANDÉS */}
        <SectionTitle className="mt-5">Documents demandés</SectionTitle>
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="grid grid-cols-2 gap-2">
            {DOCS.map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid={`vf-doc-${d}`} checked={docsDemandes.includes(d)} onChange={() => toggleDoc(d)} /> {d}
              </label>
            ))}
          </div>
          <div className="mt-3">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Si locataire</div>
            <div className="grid grid-cols-2 gap-2">
              {DOCS_LOCATAIRE.map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" data-testid={`vf-doc-${d}`} checked={docsDemandes.includes(d)} onChange={() => toggleDoc(d)} /> {d}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* PARTIE 4 — COMMENTAIRES */}
        <SectionTitle className="mt-5">Commentaires</SectionTitle>
        <div className="rounded-lg border border-slate-200 p-3">
          {comments.length === 0 ? (
            <p className="text-sm text-slate-400">Aucun commentaire pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {comments.map((cm) => (
                <div key={cm.id} data-testid="vf-comment-item" className="rounded-lg bg-slate-50 p-2.5">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <MessageSquare className="h-3 w-3" /> {cm.auteur_nom} · {formatDateTime(cm.date)}
                  </div>
                  <div className="mt-0.5 whitespace-pre-wrap text-sm text-slate-800">{cm.texte}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-start gap-2">
            <textarea data-testid="vf-comment-input" rows={2} className="ef flex-1" placeholder="Ajouter un commentaire…" value={newComment} onChange={(e) => setNewComment(e.target.value)} />
            <button type="button" data-testid="vf-comment-add" onClick={addComment} disabled={addingComment || !newComment.trim()} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50">
              {addingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Ajouter
            </button>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
          <button data-testid={isEdit ? "edit-visite-save" : "vf-submit"} disabled={loading} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isEdit ? "Enregistrer" : "Créer le rendez-vous"}
          </button>
        </div>
        <style>{`.ef{width:100%;border:1px solid #e2e8f0;border-radius:.5rem;padding:.5rem .625rem;font-size:.875rem;outline:none}.ef:focus{border-color:#94a3b8}`}</style>
      </form>
    </div>
  );
}

function SectionTitle({ children, className = "" }) {
  return <div className={`mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 ${className}`}>{children}</div>;
}
function Field({ label, children }) {
  return <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>{children}</div>;
}
function addHour(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, m + 60);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function CpVille({ cp, ville, onCp, onVille }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!cp || cp.length !== 5) { setOpts([]); return; }
    let active = true;
    fetch(`https://geo.api.gouv.fr/communes?codePostal=${cp}&fields=nom&format=json`)
      .then((r) => r.json())
      .then((d) => { if (!active) return; const names = Array.isArray(d) ? d.map((x) => x.nom) : []; setOpts(names); if (names.length && !ville) onVille(names[0]); })
      .catch(() => active && setOpts([]));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cp]);
  return (
    <>
      <Field label="Code postal"><input data-testid="vf-cp" className="ef" value={cp} onChange={(e) => onCp(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} placeholder="Ex : 69008" /></Field>
      <Field label="Ville"><input data-testid="vf-ville" className="ef" list="vfm-villes" value={ville} onChange={(e) => onVille(e.target.value)} placeholder="Sélectionner ou saisir" /><datalist id="vfm-villes">{opts.map((o) => <option key={o} value={o} />)}</datalist></Field>
    </>
  );
}
